/**
 * application.ts - P2 Placement Slice 13 (Antrag + Signatur + Einreichung) shared contract.
 *
 * The status model 10.3 (the concept's DB names verbatim; Slice-14/15 values reserved, verworfen
 * added as the Slice-13 terminal), the three signature channels, the form provenance, the signed
 * quelle, the esign tier, the outbound mail arten, the prefill variable catalog + bag, the events,
 * and the broker Tab-5a + Kundenportal wire types. ASCII hyphens only (the no-restricted-syntax
 * lint rule on packages/shared/src/**).
 */

import type { PlacementStatus, PlacementClosureStatus } from './placement';

// ── status model 10.3 ───────────────────────────────────────────────────────────

export const APPLICATION_STATUS = {
  VORBEREITET: 'vorbereitet',
  ZUR_UNTERSCHRIFT_VERSENDET: 'zur_unterschrift_versendet',
  UNTERSCHRIEBEN_EINGEGANGEN: 'unterschrieben_eingegangen',
  EINGEREICHT: 'eingereicht',
  BEDINGUNGEN_GEAENDERT: 'bedingungen_geaendert',
  ANGENOMMEN: 'angenommen',
  ABGELEHNT: 'abgelehnt',
  DECKUNGSBESTAETIGT: 'deckungsbestaetigt',
  POLICE_ERHALTEN: 'police_erhalten',
  GEPRUEFT: 'geprueft',
  ABGESCHLOSSEN: 'abgeschlossen',
  VERWORFEN: 'verworfen',
} as const;
export type ApplicationStatus = (typeof APPLICATION_STATUS)[keyof typeof APPLICATION_STATUS];

/** The 12 statuses in 10.3 order, verworfen last. */
export const APPLICATION_STATUS_VALUES: readonly ApplicationStatus[] = [
  'vorbereitet',
  'zur_unterschrift_versendet',
  'unterschrieben_eingegangen',
  'eingereicht',
  'bedingungen_geaendert',
  'angenommen',
  'abgelehnt',
  'deckungsbestaetigt',
  'police_erhalten',
  'geprueft',
  'abgeschlossen',
  'verworfen',
];

export const APPLICATION_STATUS_LABELS: Record<ApplicationStatus, string> = {
  vorbereitet: 'Vorbereitet',
  zur_unterschrift_versendet: 'Zur Unterschrift versendet',
  unterschrieben_eingegangen: 'Unterschrieben eingegangen',
  eingereicht: 'Eingereicht',
  bedingungen_geaendert: 'Bedingungen geaendert',
  angenommen: 'Angenommen',
  abgelehnt: 'Abgelehnt',
  deckungsbestaetigt: 'Deckungsbestaetigt',
  police_erhalten: 'Police erhalten',
  geprueft: 'Geprueft',
  abgeschlossen: 'Abgeschlossen',
  verworfen: 'Verworfen',
};

/** 10.3 edges reachable in Slice 13; the Slice-14/15 edges are listed but their endpoints have no Slice-13 writer. */
export const APPLICATION_STATUS_TRANSITIONS: Record<ApplicationStatus, readonly ApplicationStatus[]> = {
  vorbereitet: ['zur_unterschrift_versendet', 'verworfen'],
  zur_unterschrift_versendet: ['unterschrieben_eingegangen', 'verworfen'],
  unterschrieben_eingegangen: ['eingereicht', 'verworfen'],
  eingereicht: ['bedingungen_geaendert', 'angenommen', 'abgelehnt'], // Slice 14
  bedingungen_geaendert: ['eingereicht', 'abgelehnt'], // Slice 14 (E6)
  angenommen: ['deckungsbestaetigt'],
  abgelehnt: [],
  deckungsbestaetigt: ['police_erhalten'],
  police_erhalten: ['geprueft'],
  geprueft: ['abgeschlossen'],
  abgeschlossen: [],
  verworfen: [],
};

export function canTransitionApplication(from: ApplicationStatus, to: ApplicationStatus): boolean {
  return APPLICATION_STATUS_TRANSITIONS[from].includes(to);
}

export const APPLICATION_TERMINAL_STATUSES: readonly ApplicationStatus[] = ['verworfen', 'abgelehnt', 'abgeschlossen'];

/** Slice-13-reachable statuses = the ones the Tab-5a actions understand. */
export const APPLICATION_SLICE13_STATUSES: readonly ApplicationStatus[] = [
  'vorbereitet',
  'zur_unterschrift_versendet',
  'unterschrieben_eingegangen',
  'eingereicht',
  'verworfen',
];

/** Slice-15-reachable statuses = the Policenkontrolle + Abschluss chain tail. */
export const APPLICATION_SLICE15_STATUSES: readonly ApplicationStatus[] = ['geprueft', 'abgeschlossen'];

// ── signature channel / form quelle / signed quelle / esign tier / mail art ───────

export const SIGNATURE_CHANNEL = { PORTAL: 'portal', EMAIL: 'email', ESIGN: 'esign' } as const;
export type SignatureChannel = (typeof SIGNATURE_CHANNEL)[keyof typeof SIGNATURE_CHANNEL];
export const SIGNATURE_CHANNEL_VALUES: readonly SignatureChannel[] = ['portal', 'email', 'esign'];
export const SIGNATURE_CHANNEL_LABELS: Record<SignatureChannel, string> = {
  portal: 'Kundenportal',
  email: 'E-Mail',
  esign: 'Elektronische Signatur',
};

export const APPLICATION_FORM_QUELLE = { VERSICHERER_FORMULAR: 'versicherer_formular', BROKLY: 'brokly' } as const;
export type ApplicationFormQuelle = (typeof APPLICATION_FORM_QUELLE)[keyof typeof APPLICATION_FORM_QUELLE];
export const APPLICATION_FORM_QUELLE_LABELS: Record<ApplicationFormQuelle, string> = {
  versicherer_formular: 'Versicherer-Formular',
  brokly: 'Brokly-Antrag',
};

export const SIGNED_QUELLE = { PORTAL_UPLOAD: 'portal_upload', BROKER_UPLOAD: 'broker_upload', ESIGN_DOWNLOAD: 'esign_download' } as const;
export type SignedQuelle = (typeof SIGNED_QUELLE)[keyof typeof SIGNED_QUELLE];
export const SIGNED_QUELLE_LABELS: Record<SignedQuelle, string> = {
  portal_upload: 'Kundenportal',
  broker_upload: 'Upload Broker',
  esign_download: 'E-Signatur',
};

export const ESIGN_TIER = { SES: 'ses', FES: 'fes', QES: 'qes' } as const;
export type EsignTier = (typeof ESIGN_TIER)[keyof typeof ESIGN_TIER];
export const ESIGN_TIER_LABELS: Record<EsignTier, string> = {
  ses: 'Einfache elektronische Signatur',
  fes: 'Fortgeschrittene elektronische Signatur',
  qes: 'Qualifizierte elektronische Signatur',
};
export const ANTRAG_ESIGN_TIER_DEFAULT: EsignTier = 'fes';

export const APPLICATION_EMAIL_ART = {
  SIGNATUR_VERSAND: 'signatur_versand',
  SIGNATUR_ERNEUT: 'signatur_erneut',
  EINREICHUNG: 'einreichung',
  BEDINGUNGEN_VERSAND: 'bedingungen_versand',
  ALTERNATIVE_VERSAND: 'alternative_versand',
} as const;
export type ApplicationEmailArt = (typeof APPLICATION_EMAIL_ART)[keyof typeof APPLICATION_EMAIL_ART];
export const APPLICATION_EMAIL_ART_LABELS: Record<ApplicationEmailArt, string> = {
  signatur_versand: 'Zur Unterschrift versendet',
  signatur_erneut: 'Erneut zur Unterschrift versendet',
  einreichung: 'Bei Versicherer eingereicht',
  bedingungen_versand: 'Geaenderte Bedingungen an Kunden',
  alternative_versand: 'Alternativofferte an Kunden',
};

export type EsignRequestStatusWire = 'pending' | 'partially' | 'completed' | 'declined';

export const ANTRAG_ERLAUBTE_PLACEMENT_STATUS: readonly PlacementStatus[] = ['entschieden'];
export const ANTRAG_SIGNATUR_FRIST_DEFAULT_ARBEITSTAGE = 10;
export const ANTRAG_VERSAND_LOCK_MINUTES = 10;
export const ANTRAG_BEMERKUNG_MAX = 2000;
export const ANTRAG_VERWERFEN_GRUND_MIN = 3;

export type AntragSperrgrund =
  | 'placement_locked'
  | 'placement_status_invalid'
  | 'kein_kundenentscheid'
  | 'antrag_offen'
  | 'alternative_offen';

export type AntragAktionSperrgrund = 'placement_locked' | 'placement_status_invalid';

/** Pure: why ANY Antrag action (not just vorbereiten) is refused on this placement (null = allowed).
 *  Closure first (it's the stronger lock), then the status allow-list. */
export function antragAktionSperrgrund(input: {
  placementStatus: PlacementStatus;
  closureStatus: PlacementClosureStatus;
}): AntragAktionSperrgrund | null {
  if (input.closureStatus !== 'offen') return 'placement_locked';
  if (!ANTRAG_ERLAUBTE_PLACEMENT_STATUS.includes(input.placementStatus)) return 'placement_status_invalid';
  return null;
}

/** Pure: why «Antrag vorbereiten» is refused (null = allowed). */
export function antragVorbereitenSperrgrund(input: {
  placementStatus: PlacementStatus;
  closureStatus: PlacementClosureStatus;
  hatAngenommen: boolean;
  aktuellStatus: ApplicationStatus | null;
  alternativeRundeOffen?: boolean;
}): AntragSperrgrund | null {
  const aktionGrund = antragAktionSperrgrund({
    placementStatus: input.placementStatus,
    closureStatus: input.closureStatus,
  });
  if (aktionGrund !== null) return aktionGrund;
  if (!input.hatAngenommen) return 'kein_kundenentscheid';
  // A non-terminal aktuell application blocks a new one: verwerfen first. verworfen/terminal = allowed.
  if (input.aktuellStatus !== null && !APPLICATION_TERMINAL_STATUSES.includes(input.aktuellStatus)) {
    return 'antrag_offen';
  }
  // Slice 14 (E7): an abgelehnt Antrag with an open alternativofferte round waits for the customer's answer.
  if (input.aktuellStatus === 'abgelehnt' && input.alternativeRundeOffen === true) {
    return 'alternative_offen';
  }
  return null;
}

/** Pure: derived overdue flag (badge correct before the hourly tick). */
export function isSignaturUeberfaellig(
  status: ApplicationStatus,
  fristDate: string | null,
  todayIso: string,
): boolean {
  if (status !== 'zur_unterschrift_versendet') return false;
  if (!fristDate) return false;
  return fristDate < todayIso;
}

/** The flat variable catalog of the prefill bag (the template path consumes these keys; risiko_* / soll_* are dynamic). */
export const ANTRAG_VARIABLEN_KEYS = [
  'kunde_name',
  'kunde_anrede',
  'kunde_vorname',
  'kunde_nachname',
  'kunde_adresse',
  'kunde_uid',
  'kunde_telefon',
  'kunde_email',
  'kunde_ansprechperson',
  'placement_number',
  'sparte_code',
  'sparte_label',
  'anlass_label',
  'versicherungsobjekt',
  'versicherer_name',
  'versicherer_uid',
  'submission_number',
  'offerte_version',
  'praemie_jahr',
  'versicherungssumme',
  'selbstbehalt',
  'vertragsdauer_monate',
  'beginn',
  'entscheider_name',
  'entscheid_datum',
  'broker_firma',
  'berater_name',
  'datum',
  'bemerkung',
] as const;
export type AntragVariableKey = (typeof ANTRAG_VARIABLEN_KEYS)[number];

export interface AntragPrefill {
  variablen: Record<string, string | null>; // ANTRAG_VARIABLEN_KEYS + risiko_<key> (+ `_<n>` suffix per object when > 1) + soll_<rule_key>
  objekte: Array<{
    insurance_object_ref: string | null;
    object_label: string | null;
    felder: Array<{ key: string; label: string; wert: string }>;
  }>;
  soll: Array<{ rule_key: string; label: string; pflicht_status: string; zielwert: string | null; selbstbehalt: string | null }>;
  hinweise: string[];
}

export const APPLICATION_EVENTS = {
  PREPARED: 'application.prepared',
  UPDATED: 'application.updated',
  SENT_FOR_SIGNATURE: 'application.sent_for_signature',
  RESENT_FOR_SIGNATURE: 'application.resent_for_signature',
  SIGNED_RECEIVED: 'application.signed_received',
  SUBMITTED: 'application.submitted',
  SIGNATURE_OVERDUE: 'application.signature_overdue',
  BOUNCED: 'application.bounced',
  ESIGN_DECLINED: 'application.esign_declined',
  CARRIER_REPLY_RECEIVED: 'application.carrier_reply_received',
  DISCARDED: 'application.discarded',
  // Slice 14 (Underwriting / Versicherer-Rueckmeldung)
  ACCEPTED: 'application.accepted',
  REJECTED: 'application.rejected',
  CONDITION_CHANGED: 'application.condition_changed',
  CONDITION_ACCEPTED: 'application.condition_accepted',
  CONDITION_REJECTED: 'application.condition_rejected',
  CONDITION_OVERDUE: 'application.condition_overdue',
  ALTERNATIVE_PROPOSED: 'application.alternative_proposed',
  FALLBACK_RECORDED: 'application.fallback_recorded',
  COVERAGE_CONFIRMED: 'application.coverage_confirmed',
  POLICE_RECEIVED: 'application.police_received',
  // Slice 15 (Policenkontrolle + Abschluss)
  CONTROLLED: 'application.controlled',
  COMPLETED: 'application.completed',
} as const;
export type ApplicationEvent = (typeof APPLICATION_EVENTS)[keyof typeof APPLICATION_EVENTS];

// ── Wire types (broker Tab 5a) ────────────────────────────────────────────────────

export interface AntragEmailEintrag {
  id: string;
  art: ApplicationEmailArt;
  art_label: string;
  empfaenger: string;
  betreff: string;
  sent_at: string;
  sent_by_name: string | null;
  bounce_at: string | null;
  bounce_grund: string | null;
  bounce_task_id: string | null;
}

export interface AntragEintrag {
  id: string;
  version_no: number;
  is_aktuell: boolean;
  status: ApplicationStatus;
  status_label: string;
  status_changed_at: string;
  decision_id: string;
  offer_id: string;
  submission_id: string;
  submission_number: string;
  carrier_uid: string;
  carrier_name: string | null;
  carrier_logo_url: string | null;
  policy_id: string | null;
  policy_number: string | null;
  begin_date: string;
  praemie_jahr: number | null;
  versicherungssumme: number | null;
  selbstbehalt: number | null;
  vertragsdauer_monate: number | null;
  bemerkung: string | null;
  form_quelle: ApplicationFormQuelle;
  form_quelle_label: string;
  carrier_form_template_ref: string | null;
  form_template_name: string | null;
  doc: { filename: string; mime: string; size_bytes: number; sha256: string; engine: string; rendered_at: string; datei_url: string; hidden_reason: 'permission' | null };
  prefill_hinweise: string[];
  signatur: {
    channel: SignatureChannel | null;
    channel_label: string | null;
    versendet_at: string | null;
    versendet_by_name: string | null;
    versand_count: number;
    frist_date: string | null;
    ueberfaellig: boolean;
    eskaliert_at: string | null;
    task_id: string | null;
    empfaenger: string | null;
    esign: {
      tier: EsignTier | null;
      provider: string | null;
      external_request_id: string | null;
      status: EsignRequestStatusWire | null;
      status_at: string | null;
    } | null;
  };
  signiert: {
    received_at: string;
    quelle: SignedQuelle;
    quelle_label: string;
    filename: string;
    mime: string;
    size_bytes: number;
    sha256: string;
    erfasst_by_name: string | null;
    datei_url: string;
    hidden_reason: 'permission' | null;
  } | null;
  einreichung: {
    submitted_at: string;
    submitted_by_name: string | null;
    empfaenger: string;
    communication_id: string;
    bounce_at: string | null;
  } | null;
  carrier_reply: { received_at: string; task_id: string | null } | null;
  versand_laeuft: boolean;
  /** PR #43 wave 2 (#5): a record_failed marker - every send on the Antrag is blocked until resolved. */
  versand_gesperrt: boolean;
  /** PR #43 wave 2 (#5): the stored comm id + a recordable status make `nachtragen` possible. */
  nachtragen_moeglich: boolean;
  letzter_versandfehler: string | null;
  letzter_versandfehler_at: string | null;
  verworfen: { at: string; by_name: string | null; grund: string } | null;
  emails: AntragEmailEintrag[];
  created_at: string;
  updated_at: string;
}

export interface AntragResponse {
  placement_id: string;
  placement_number: string;
  status: PlacementStatus;
  closure_status: PlacementClosureStatus;
  branchencode: string;
  branchen_label: string;
  kunde_name: string;
  entscheid: {
    decision_id: string;
    decided_at: string;
    carrier_uid: string;
    carrier_name: string | null;
    submission_number: string;
    offer_id: string;
    version_no: number;
    praemie_jahr: number | null;
    versicherungsbeginn: string | null;
    entscheider_name: string | null;
    promotion_policy_id: string | null;
    promotion_policy_number: string | null;
  } | null;
  antrag: AntragEintrag | null;
  versionen: AntragEintrag[];
  vorbereiten_erlaubt: boolean;
  sperrgrund: AntragSperrgrund | null;
  /** Why ANY Antrag action is refused on this placement (e.g. after an Ausschreibung abbruch). null = allowed. */
  aktion_sperrgrund: AntragAktionSperrgrund | null;
  /** Slice 14 (E7): the accepted alternativofferte the next Antrag version will be prepared against. */
  alternative: { runde_id: string; carrier_uid: string; carrier_name: string | null; submission_number: string | null; rang: number | null } | null;
  formular: {
    form_template_ref: string | null;
    template_vorhanden: boolean;
    template_name: string | null;
    quelle_vorschau: ApplicationFormQuelle;
  };
  begin_date_vorschlag: string | null;
  praemie_jahr_vorschlag: number | null;
  kanal_vorschlag: SignatureChannel;
  portal_verfuegbar: boolean;
  kunde_email: string | null;
  esign_verfuegbar: boolean;
  carrier_email: string | null;
  frist_vorschlag: string;
  aktionen: {
    bearbeiten: boolean;
    signatur_senden: boolean;
    erneut_senden: boolean;
    frist_aendern: boolean;
    status_abfragen: boolean;
    signiert_hochladen: boolean;
    einreichen: boolean;
    verwerfen: boolean;
  };
}

export interface AntragVorbereitenBody {
  begin_date?: string;
  bemerkung?: string | null;
  praemie_jahr?: number | null;
}
export type AntragAktualisierenBody = AntragVorbereitenBody;
export interface AntragSignaturBody {
  channel: SignatureChannel;
  frist_date?: string;
}
export interface AntragFristBody {
  frist_date: string;
}
export interface AntragVerwerfenBody {
  grund: string;
}

// ── Wire types (portal) ───────────────────────────────────────────────────────────

export interface PortalAntragListItem {
  id: string;
  placement_number: string;
  branchen_label: string;
  carrier_name: string | null;
  carrier_logo_url: string | null;
  status: ApplicationStatus;
  status_label: string;
  begin_date: string;
  praemie_jahr: number | null;
  versendet_at: string | null;
  frist_date: string | null;
  signed_received_at: string | null;
  submitted_at: string | null;
  hochladen_erlaubt: boolean;
  /** Slice 14: an open E6/E7 round awaits the customer's decision (list badge «Freigabe erforderlich»). */
  bedingungen_offen: boolean;
}

export interface PortalAntragDetail extends PortalAntragListItem {
  broker_name: string | null;
  berater_name: string | null;
  doc: { filename: string; mime: string; size_bytes: number };
  signiert: { filename: string; received_at: string } | null;
  bedingungen: PortalBedingungenBlock | null;
  aktionen: { hochladen: boolean; bedingungen_akzeptieren: boolean; bedingungen_ablehnen: boolean };
  hinweise: string[];
}

export interface PortalAntragSigniertResponse {
  id: string;
  status: ApplicationStatus;
  signed_received_at: string;
}

// ── Pinned user-facing strings (ASCII hyphens) ──────────────────────────────────────

export const ANTRAG_PORTAL_TITLE = (nr: string): string => `Antrag zur Unterschrift - ${nr}`;
export const ANTRAG_KEIN_FORMULAR_HINWEIS = 'Kein Versicherer-Formular hinterlegt - Brokly-Antrag verwendet';
export const ANTRAG_FORMULAR_DOCX_HINWEIS = 'Das Versicherer-Formular liegt als Word-Datei vor - bitte vor dem Versand pruefen';
export const ANTRAG_KEIN_ENTSCHEID_HINWEIS = 'Antrag erst nach dem dokumentierten Kundenentscheid moeglich';
export const ANTRAG_OFFEN_HINWEIS = 'Es gibt bereits einen aktiven Antrag - zuerst verwerfen, um eine neue Version zu erstellen';
export const ANTRAG_KEIN_PORTAL_HINWEIS = 'Kunde hat keinen aktiven Portalzugang - Zustellung per E-Mail';
export const ANTRAG_KEINE_EMAIL_HINWEIS = 'Keine E-Mail-Adresse beim Kunden hinterlegt';
export const ANTRAG_ESIGN_NICHT_VERFUEGBAR_HINWEIS =
  'Elektronische Signatur ist noch nicht angebunden (Skribble folgt) - bitte Kundenportal oder E-Mail waehlen';
export const ANTRAG_ESIGN_NOT_WIRED_HINWEIS = 'Der E-Signatur-Dienst ist konfiguriert, aber noch nicht angebunden';
export const ANTRAG_SIGNATUR_PORTAL_HINWEIS =
  'Der Kunde laedt den Antrag im Kundenportal herunter, unterschreibt ihn (ausgedruckt oder digital) und laedt die unterschriebene Datei wieder hoch';
export const ANTRAG_SIGNATUR_EMAIL_HINWEIS =
  'Der Antrag wird per E-Mail zugestellt - die unterschriebene Datei wird vom Broker hochgeladen';
export const ANTRAG_FRIST_ABGELAUFEN_HINWEIS = 'Unterschrift ueberfaellig - bitte beim Kunden nachfassen';
export const ANTRAG_CARRIER_EMAIL_FEHLT_HINWEIS = 'Beim Versicherer ist keine Einreichungs-Adresse hinterlegt';
export const ANTRAG_EINREICHEN_BESTAETIGUNG =
  'Der unterschriebene Antrag wird per E-Mail an den Versicherer gesendet. Danach folgt das Underwriting.';
export const ANTRAG_VERWERFEN_HINWEIS =
  'Der Antrag wird als verworfen markiert und bleibt in der Historie - danach kann eine neue Version vorbereitet werden';
export const ANTRAG_BOUNCE_HINWEIS = 'E-Mail-Zustellung fehlgeschlagen - Adresse pruefen und erneut senden';
export const PORTAL_ANTRAEGE_LEER_HINWEIS = 'Zurzeit liegt kein Antrag zur Unterschrift vor';
export const PORTAL_ANTRAG_HOCHLADEN_HINWEIS =
  'Bitte laden Sie den Antrag herunter, unterschreiben Sie ihn (ausgedruckt und handschriftlich oder digital) und laden Sie die unterschriebene Datei hier hoch';
export const PORTAL_ANTRAG_EINGEGANGEN_HINWEIS = 'Vielen Dank - Ihr unterschriebener Antrag ist bei Ihrem Broker eingegangen';
export const PORTAL_ANTRAG_EINGEREICHT_HINWEIS = 'Ihr Antrag wurde beim Versicherer eingereicht';

// ════════════════════════════════════════════════════════════════════════════════════
// P2 Placement Slice 14 (Underwriting / Versicherer-Rueckmeldung, Tab 5b + Kundenportal E6)
// ════════════════════════════════════════════════════════════════════════════════════

/** 10.3 edges unchanged; Slice 14 is the writer of the underwriting edges. */
export const APPLICATION_SLICE14_STATUSES: readonly ApplicationStatus[] = ['eingereicht', 'bedingungen_geaendert', 'angenommen', 'abgelehnt', 'deckungsbestaetigt', 'police_erhalten'];
export const APPLICATION_UNDERWRITING_ENTERED: readonly ApplicationStatus[] = ['eingereicht', 'bedingungen_geaendert', 'angenommen', 'abgelehnt', 'deckungsbestaetigt', 'police_erhalten', 'geprueft', 'abgeschlossen'];

/** The linear underwriting path (bedingungen_geaendert / abgelehnt / verworfen are never intermediate). */
const UW_LINEAR_CHAIN: readonly ApplicationStatus[] = ['eingereicht', 'angenommen', 'deckungsbestaetigt', 'police_erhalten', 'geprueft', 'abgeschlossen'];

/** Pure: the ordered chain of statuses to walk from `from` to `to` through APPLICATION_STATUS_TRANSITIONS
 *  (D5), e.g. eingereicht -> police_erhalten = ['angenommen','deckungsbestaetigt','police_erhalten'];
 *  null when no linear path exists. */
export function applicationStatusChain(from: ApplicationStatus, to: ApplicationStatus): ApplicationStatus[] | null {
  const fromIdx = UW_LINEAR_CHAIN.indexOf(from);
  const toIdx = UW_LINEAR_CHAIN.indexOf(to);
  if (fromIdx === -1 || toIdx === -1 || toIdx <= fromIdx) return null;
  const chain = UW_LINEAR_CHAIN.slice(fromIdx + 1, toIdx + 1);
  let cur = from;
  for (const step of chain) {
    if (!canTransitionApplication(cur, step)) return null;
    cur = step;
  }
  return [...chain];
}

export const UW_ENTSCHEID_ART = { ANGENOMMEN: 'angenommen', ABGELEHNT: 'abgelehnt' } as const;
export type UwEntscheidArt = (typeof UW_ENTSCHEID_ART)[keyof typeof UW_ENTSCHEID_ART];
export const ABGELEHNT_QUELLE = { VERSICHERER: 'versicherer', KUNDE: 'kunde' } as const;
export type AbgelehntQuelle = (typeof ABGELEHNT_QUELLE)[keyof typeof ABGELEHNT_QUELLE];
export const ABGELEHNT_QUELLE_LABELS: Record<AbgelehntQuelle, string> = { versicherer: 'Durch Versicherer abgelehnt', kunde: 'Kunde lehnt geaenderte Bedingungen ab' };
export const UW_RUNDE_ART = { BEDINGUNGEN_GEAENDERT: 'bedingungen_geaendert', ALTERNATIVOFFERTE: 'alternativofferte' } as const;
export type UwRundeArt = (typeof UW_RUNDE_ART)[keyof typeof UW_RUNDE_ART];
export const UW_RUNDE_ART_LABELS: Record<UwRundeArt, string> = { bedingungen_geaendert: 'Geaenderte Bedingungen', alternativofferte: 'Alternativofferte' };
export const UW_RUNDE_STATUS = { OFFEN: 'offen', AKZEPTIERT: 'akzeptiert', ABGELEHNT: 'abgelehnt', HINFAELLIG: 'hinfaellig' } as const;
export type UwRundeStatus = (typeof UW_RUNDE_STATUS)[keyof typeof UW_RUNDE_STATUS];
export const UW_RUNDE_STATUS_VALUES: readonly UwRundeStatus[] = ['offen', 'akzeptiert', 'abgelehnt', 'hinfaellig'];
export const UW_RUNDE_STATUS_LABELS: Record<UwRundeStatus, string> = { offen: 'Beim Kunden - offen', akzeptiert: 'Vom Kunden akzeptiert', abgelehnt: 'Vom Kunden abgelehnt', hinfaellig: 'Hinfaellig' };
export const UW_RUNDE_CHANNEL_VALUES: readonly ('portal' | 'email')[] = ['portal', 'email'];
export const UW_KUNDE_ENTSCHEID = { AKZEPTIERT: 'akzeptiert', ABGELEHNT: 'abgelehnt' } as const;
export type UwKundeEntscheid = (typeof UW_KUNDE_ENTSCHEID)[keyof typeof UW_KUNDE_ENTSCHEID];
export const FALLBACK_ART = { ALTERNATIVOFFERTE: 'alternativofferte', GLEICHER_VERSICHERER: 'gleicher_versicherer', ABBRUCH: 'abbruch' } as const;
export type FallbackArt = (typeof FALLBACK_ART)[keyof typeof FALLBACK_ART];
export const FALLBACK_ART_VALUES: readonly FallbackArt[] = ['alternativofferte', 'gleicher_versicherer', 'abbruch'];
export const FALLBACK_ART_LABELS: Record<FallbackArt, string> = { alternativofferte: 'Rueckgriff auf Alternativofferte', gleicher_versicherer: 'Neuer Antrag beim gleichen Versicherer', abbruch: 'Ausschreibung abbrechen' };
/** The E6 / E7 delta fields (the *_alt / *_neu pairs) in display order. */
export const UW_DELTA_FELDER = ['praemie_jahr', 'versicherungssumme', 'selbstbehalt', 'begin_date', 'vertragsdauer_monate'] as const;
export type UwDeltaFeld = (typeof UW_DELTA_FELDER)[number];
export const UW_DELTA_FELD_LABELS: Record<UwDeltaFeld, string> = { praemie_jahr: 'Jahrespraemie', versicherungssumme: 'Versicherungssumme', selbstbehalt: 'Selbstbehalt', begin_date: 'Versicherungsbeginn', vertragsdauer_monate: 'Vertragsdauer (Monate)' };
export const UW_BEDINGUNGEN_FRIST_DEFAULT_ARBEITSTAGE = 10;
export const UW_BEDINGUNGEN_TEXT_MIN = 3;
export const UW_BEDINGUNGEN_TEXT_MAX = 4000;
export const UW_GRUND_MIN = 3;
export const UW_AUSSCHLUESSE_MAX = 20;
export const UW_POLICY_NUMBER_MAX = 100;

/** Pure: derived overdue flag of an offen round. */
export function isRundeUeberfaellig(status: UwRundeStatus, fristDate: string, todayIso: string): boolean {
  if (status !== 'offen') return false;
  return fristDate < todayIso;
}

/** Pure: the changed fields of a round (neu !== null AND neu !== alt), for the audit list + the UI delta table. */
export function rundeGeaenderteFelder(r: {
  praemie_jahr_alt: number | null;
  praemie_jahr_neu: number | null;
  versicherungssumme_alt: number | null;
  versicherungssumme_neu: number | null;
  selbstbehalt_alt: number | null;
  selbstbehalt_neu: number | null;
  begin_date_alt: string | null;
  begin_date_neu: string | null;
  vertragsdauer_monate_alt: number | null;
  vertragsdauer_monate_neu: number | null;
  ausschluesse_neu: string[];
}): UwDeltaFeld[] {
  const out: UwDeltaFeld[] = [];
  if (r.praemie_jahr_neu !== null && r.praemie_jahr_neu !== r.praemie_jahr_alt) out.push('praemie_jahr');
  if (r.versicherungssumme_neu !== null && r.versicherungssumme_neu !== r.versicherungssumme_alt) out.push('versicherungssumme');
  if (r.selbstbehalt_neu !== null && r.selbstbehalt_neu !== r.selbstbehalt_alt) out.push('selbstbehalt');
  if (r.begin_date_neu !== null && r.begin_date_neu !== r.begin_date_alt) out.push('begin_date');
  if (r.vertragsdauer_monate_neu !== null && r.vertragsdauer_monate_neu !== r.vertragsdauer_monate_alt) out.push('vertragsdauer_monate');
  return out;
}

// ── Wire types (broker Tab 5b) ────────────────────────────────────────────────────

export interface UwRundeEintrag {
  id: string;
  runde_no: number;
  art: UwRundeArt;
  art_label: string;
  status: UwRundeStatus;
  status_label: string;
  status_changed_at: string;
  bedingungen_text: string;
  carrier_communication_id: string | null;
  alternative: { offer_id: string; submission_id: string; submission_number: string; carrier_uid: string; carrier_name: string | null; carrier_logo_url: string | null; rang: number } | null;
  delta: { feld: UwDeltaFeld; label: string; alt: string | number | null; neu: string | number | null }[];
  geaenderte_felder: UwDeltaFeld[];
  ausschluesse_neu: string[];
  zustellung: {
    channel: 'portal' | 'email';
    channel_label: string;
    zugestellt_at: string;
    zugestellt_by_name: string | null;
    empfaenger: string | null;
    portal_delivery_id: string | null;
    email_id: string | null;
    frist_date: string;
    ueberfaellig: boolean;
    eskaliert_at: string | null;
    task_id: string | null;
  };
  entscheid: {
    entschieden_at: string;
    entscheid: UwKundeEntscheid;
    entscheider_art: 'kunde_portal' | 'broker_erfasst';
    entscheider_art_label: string;
    entscheider_name: string;
    response_channel: string;
    response_channel_label: string;
    erfasst_by_name: string | null;
    ablehnung_grund: string | null;
  } | null;
  erfasst_by_name: string | null;
  created_at: string;
}
export interface UwAlternativeEintrag {
  offer_id: string;
  submission_id: string;
  submission_number: string;
  carrier_uid: string;
  carrier_name: string | null;
  carrier_logo_url: string | null;
  rang: number;
  score_total: number;
  ampel: 'gruen' | 'gelb' | 'rot' | 'grau';
  e4_flag: boolean;
  version_no: number | null;
  praemie_jahr: number | null;
  versicherungssumme: number | null;
  selbstbehalt: number | null;
  beginn: string | null;
  vertragsdauer_monate: number | null;
  staerken: string[];
  schwaechen: string[];
}
export interface UnderwritingResponse {
  placement_id: string;
  placement_number: string;
  status: PlacementStatus;
  closure_status: PlacementClosureStatus;
  branchen_label: string;
  kunde_name: string;
  antrag: {
    id: string;
    version_no: number;
    status: ApplicationStatus;
    status_label: string;
    status_changed_at: string;
    carrier_uid: string;
    carrier_name: string | null;
    carrier_logo_url: string | null;
    submission_id: string;
    submission_number: string;
    offer_id: string;
    begin_date: string;
    praemie_jahr: number | null;
    versicherungssumme: number | null;
    selbstbehalt: number | null;
    vertragsdauer_monate: number | null;
    submitted_at: string | null;
    submitted_to_email: string | null;
    vorgaenger_application_id: string | null;
    alternative_runde_id: string | null;
  } | null;
  carrier_reply: { received_at: string; communication_id: string; task_id: string | null; task_open: boolean } | null;
  entscheid: {
    art: UwEntscheidArt;
    at: string;
    by_name: string | null;
    grund: string | null;
    implizit: boolean;
    communication_id: string | null;
    abgelehnt_quelle: AbgelehntQuelle | null;
    abgelehnt_quelle_label: string | null;
  } | null;
  deckung: { at: string; by_name: string | null; bemerkung: string | null; implizit: boolean; communication_id: string | null } | null;
  police: { at: string; by_name: string | null; policy_number: string; policy_id: string | null; policy_bezeichnung: string | null; communication_id: string | null } | null;
  fallback: { art: FallbackArt; art_label: string; at: string; by_name: string | null; grund: string | null } | null;
  runden: UwRundeEintrag[];
  offene_runde_id: string | null;
  akzeptierte_alternative_runde_id: string | null;
  alternativen: UwAlternativeEintrag[];
  alternativen_hinweis: string | null;
  e7_task_id: string | null;
  kanal_vorschlag: 'portal' | 'email';
  portal_verfuegbar: boolean;
  kunde_email: string | null;
  frist_vorschlag: string;
  versand_laeuft: boolean;
  /** PR #43 wave 2 (#5): a record_failed marker on the Antrag blocks the underwriting sends until resolved. */
  versand_gesperrt: boolean;
  aktionen: {
    annehmen: boolean;
    ablehnen: boolean;
    bedingungen_erfassen: boolean;
    runde_entscheid_erfassen: boolean;
    alternative_vorschlagen: boolean;
    fallback_erfassen: boolean;
    abbrechen: boolean;
    antrag_vorbereiten: boolean;
    deckung_bestaetigen: boolean;
    police_erhalten: boolean;
  };
  hinweise: string[];
}
export interface UwEntscheidBody {
  grund?: string | null;
  communication_id?: string | null;
}
export interface UwBedingungenBody {
  bedingungen_text: string;
  praemie_jahr_neu?: number | null;
  versicherungssumme_neu?: number | null;
  selbstbehalt_neu?: number | null;
  begin_date_neu?: string | null;
  vertragsdauer_monate_neu?: number | null;
  ausschluesse_neu?: string[];
  channel: 'portal' | 'email';
  frist_date?: string;
  communication_id?: string | null;
}
export interface UwRundeEntscheidBody {
  entscheid: UwKundeEntscheid;
  response_channel: 'email' | 'telefon' | 'persoenlich';
  entscheider_name: string;
  grund?: string | null;
}
export interface UwAlternativeBody {
  offer_id: string;
  channel: 'portal' | 'email';
  frist_date?: string;
  bemerkung?: string | null;
}
export interface UwFallbackBody {
  art: 'gleicher_versicherer' | 'abbruch';
  grund: string;
}
export interface UwDeckungBody {
  bemerkung?: string | null;
  communication_id?: string | null;
}
export interface UwPoliceErhaltenBody {
  policy_number: string;
  communication_id?: string | null;
}

// ── Wire types (portal E6/E7) ──────────────────────────────────────────────────────

export interface PortalBedingungenBlock {
  runde_id: string;
  art: UwRundeArt;
  art_label: string;
  status: UwRundeStatus;
  bedingungen_text: string;
  delta: UwRundeEintrag['delta'];
  ausschluesse_neu: string[];
  alternative: { carrier_name: string | null; carrier_logo_url: string | null; rang: number } | null;
  frist_date: string;
  zugestellt_at: string;
  entschieden_at: string | null;
  entscheid: UwKundeEntscheid | null;
}
export interface PortalBedingungenAkzeptierenBody {
  bestaetigung: true;
}
export interface PortalBedingungenAblehnenBody {
  grund: string;
}

// ── Pinned user-facing strings (ASCII hyphens) ──────────────────────────────────────

export const UW_BEDINGUNGEN_PORTAL_TITLE = (nr: string): string => `Geaenderte Bedingungen zu Ihrem Antrag - ${nr}`;
export const UW_ALTERNATIVE_PORTAL_TITLE = (nr: string): string => `Alternativofferte zu Ihrem Antrag - ${nr}`;
export const UW_ALTERNATIVE_TEXT_DEFAULT = (carrier: string, rang: number): string => `Der Versicherer hat den Antrag abgelehnt. Ihr Broker schlaegt die Offerte von ${carrier} (Rang ${rang} im Vergleich) als Alternative vor.`;
export const UW_IMPLIZIT_DECKUNG = 'Implizit angenommen - Deckungsbestaetigung erhalten';
export const UW_IMPLIZIT_POLICE = 'Implizit bestaetigt - Police erhalten';
export const UW_NOCH_NICHT_EINGEREICHT_HINWEIS = 'Das Underwriting beginnt mit der Einreichung des Antrags beim Versicherer (Tab Antrag)';
export const UW_RUECKMELDUNG_HINWEIS = 'Rueckmeldung des Versicherers eingegangen - bitte Mail lesen und das Ergebnis hier erfassen';
export const UW_KEINE_RUECKMELDUNG_HINWEIS = 'Noch keine Rueckmeldung des Versicherers - das Ergebnis kann trotzdem manuell erfasst werden';
export const UW_AKZEPTANZ_MELDEN_HINWEIS = 'Der Kunde hat die geaenderten Bedingungen akzeptiert - bitte dem Versicherer im Posteingang antworten';
export const UW_RUNDE_OFFEN_HINWEIS = 'Die Freigabe des Kunden steht noch aus';
export const UW_RUNDE_UEBERFAELLIG_HINWEIS = 'Freigabefrist abgelaufen - bitte beim Kunden nachfassen';
export const UW_ABGELEHNT_HINWEIS = 'Der Antrag wurde abgelehnt - Alternativoption waehlen, neuen Antrag beim gleichen Versicherer vorbereiten oder die Ausschreibung abbrechen';
export const UW_KEINE_ALTERNATIVEN_HINWEIS = 'Keine freigegebene Alternativofferte im Vergleich - neuer Antrag beim gleichen Versicherer oder Abbruch';
export const UW_ALTERNATIVE_AKZEPTIERT_HINWEIS = 'Der Kunde hat die Alternative akzeptiert - jetzt den neuen Antrag vorbereiten (Tab Antrag)';
export const UW_ABBRUCH_BESTAETIGUNG = 'Die Ausschreibung wird abgebrochen (Antrag abgelehnt ohne Alternative). Dieser Schritt kann nicht rueckgaengig gemacht werden.';
export const UW_POLICE_VERKNUEPFT_HINWEIS = 'Police im System gefunden und verknuepft';
export const UW_POLICE_NICHT_GEFUNDEN_HINWEIS = 'Noch keine Police mit dieser Nummer im System - sie wird bei der Policenkontrolle verknuepft';
export const UW_DECKUNG_HINWEIS = 'Deckung bestaetigt - die Police folgt ueber den Policeneingang';
export const PORTAL_BEDINGUNGEN_HINWEIS = 'Der Versicherer hat die Bedingungen Ihres Antrags geaendert. Bitte pruefen Sie die Aenderungen und akzeptieren oder lehnen Sie diese ab.';
export const PORTAL_ALTERNATIVE_HINWEIS = 'Der Versicherer hat Ihren Antrag abgelehnt. Ihr Broker schlaegt Ihnen eine Alternativofferte vor - bitte akzeptieren oder ablehnen.';
export const PORTAL_BEDINGUNGEN_AKZEPTIERT_HINWEIS = 'Vielen Dank - Ihre Zustimmung ist bei Ihrem Broker eingegangen';
export const PORTAL_BEDINGUNGEN_ABGELEHNT_HINWEIS = 'Sie haben abgelehnt - Ihr Broker meldet sich bei Ihnen';
export const PORTAL_BEDINGUNGEN_BESTAETIGUNG = 'Mit dem Akzeptieren stimmen Sie den angezeigten Bedingungen verbindlich zu.';
